import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Carousel } from 'antd';
import LoadMessage from './LoadMessage';
import Version from './VersionInfo';
import './SplashPage.css';
import logoImage from '../assets/images/busable-logo-1.svg';
import healthcareTransportIcon from '../assets/icons/healthcare_transport.png';
import schoolBusTransportImage from '../assets/images/school-bus-transport.png';
import publicBusSolutionImage from '../assets/images/public-bus-solution.png';
import charterBookingToursImage from '../assets/images/charter-booking-tours.png';
import railReplacementBusImage from '../assets/images/rail-replacement-bus.png';
import onDemandShuttleImage from '../assets/images/on-demand-shuttle.png';

function SplashPage({ messageApi, modelService, isAuthenticating, operator }) {
  const history = useHistory();
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('Initializing...');
  const [loadingErrors, setLoadingErrors] = useState({});
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState(0);
  const [currentFooterMessage, setCurrentFooterMessage] = useState(0);
  const [showSlowConnection, setShowSlowConnection] = useState(false);
  
  // Hover states for interactive elements (only logo needs custom handling)
  const [hoverStates, setHoverStates] = useState({
    logo: false
  });

  // Hover handlers (only for logo)
  const handleHover = (element, isHovering) => {
    setHoverStates(prev => ({
      ...prev,
      [element]: isHovering
    }));
  };

  // Rotating loading detail messages
  const loadingMessages = [
    "Loading route data...",
    "Retrieving schedule info...",
    "Synchronizing with dispatcher...",
    "Preparing compliance checks...",
    "Loading vehicle information...",
    "Connecting to tracking systems...",
    "Initializing rostering data...",
    "Loading customer information...",
    "Preparing reporting tools...",
    "Setting up communication channels..."
  ];

  // Professional witty footer messages for Australian bus operators
  const footerMessages = [
    "Optimizing your experience...",
    "Ensuring reliable service...",
    "Finalizing details...",
    "Almost ready...",
    "Preparing your command centre...",
    "Loading your digital tools...",
    "Setting up your operations hub...",
    "Preparing your fleet management...",
    "Loading your compliance dashboard..."
  ];

  // Busable solutions carousel
  const carouselSlides = [
    {
      name: "School Transport",
      description: "Complete school transport management with Planable™ route planning, compliance tracking, and real-time monitoring for safe student transportation across regional and urban areas.",
      icon: schoolBusTransportImage
    },
    {
      name: "Public Transport",
      description: "Efficient public transport operations with smart scheduling, passenger analytics, and integrated fare management systems powered by Busable's comprehensive platform.",
      icon: publicBusSolutionImage
    },
    {
      name: "Charter Services",
      description: "Professional charter booking and management with automated workflows, customer portals, and revenue optimization tools for tour operators and event transport.",
      icon: charterBookingToursImage
    },
    {
      name: "Rail Replacement",
      description: "Efficiently manage rail replacement services with real-time coordination, route optimization, and seamless passenger communication during service disruptions.",
      icon: railReplacementBusImage
    },
    {
      name: "On-Demand & Shuttles",
      description: "Flexible transportation solutions that adapt to real-time demand with intelligent routing, dynamic scheduling, and responsive passenger pickup systems.",
      icon: onDemandShuttleImage
    }
  ];

  const progressListener = useMemo(() => ({
    onProgressUpdate: (modelName, stage, progress) => {
      if (modelName === 'overall') {
        setLoadingProgress(progress);
        if (stage === 'fallback_loading') {
          setCurrentStage(`API Fallback Loading... ${Math.round(progress)}%`);
        } else if (stage === 'websocket_loading') {
          setCurrentStage(`WebSocket Loading... ${Math.round(progress)}%`);
        } else {
          setCurrentStage(`Loading... ${Math.round(progress)}%`);
        }
      } else if (modelName !== 'system') {
        // Track individual model progress
        setCurrentStage(`${modelName}: ${stage}`);
      }
    },
    onError: (modelName, error) => {
      console.log(`Error in ${modelName}:`, error);
      
      // Special handling for WebSocket/system errors
      if (modelName === 'system' || error.message?.includes('WebSocket')) {
        setCurrentStage('WebSocket failed - using fallback...');
        setLoadingProgress(0); // Reset progress for fallback
        setLoadingErrors(prev => ({
          ...prev,
          websocket: 'WebSocket connection failed - using API fallback'
        }));
      } else {
        setLoadingErrors(prev => ({
          ...prev,
          [modelName]: error.message || error
        }));
      }
    },
    loaded: () => {
      console.log('All models loaded - setting state to loaded');
      setLoadingProgress(100);
      setCurrentStage('Ready!');
    }
  }), []); 

  useEffect(() => {
    if (!modelService) return;
    // Set up loading progress listener

    modelService.addLoadedListener(progressListener);

    return () => {
      try {
        modelService.removeLoadedListener(progressListener);
      } catch (error) {
        console.log('Error removing listener:', error);
      }
    };
  }, [modelService, progressListener]);

  // Timer effect - count up in seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Show slow connection warning after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loadingProgress < 50) {
        setShowSlowConnection(true);
      }
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [loadingProgress]);

  // Rotating loading messages effect - commented out, using simple message from desktop file
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setCurrentLoadingMessage(prev => (prev + 1) % loadingMessages.length);
  //   }, 3000);
  //   
  //   return () => clearInterval(interval);
  // }, [loadingMessages.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFooterMessage(prev => (prev + 1) % footerMessages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [footerMessages.length]);

  // Redirect logic - only redirect when loading is complete and we have an operator
  useEffect(() => {
    if (loadingProgress === 100 && operator && !isAuthenticating) {
      // Add a small delay before redirecting to show the splash page
      const timer = setTimeout(() => {
        const redirectPath = operator?.opts?.external?.optibus?.apiKey ? "/bookings" : "/services";
        history.push(redirectPath);
      }, 500); // 500ms delay

      return () => clearTimeout(timer);
    }
  }, [loadingProgress, operator, isAuthenticating, history]);

  // Ant Design Carousel handles rotation automatically

  // React-based hover effects with instant state changes
  
  // Show loading issues if any
  const renderErrors = useMemo(() => {
    if (Object.keys(loadingErrors).length === 0) return null;

    return (
      <div style={{ 
        marginTop: '20px', 
        padding: '12px', 
        backgroundColor: '#f0f8ff', 
        border: '1px solid #b3d9ff',
        borderRadius: '6px',
        maxWidth: '400px',
        margin: '20px auto'
      }}>
        {Object.entries(loadingErrors).map(([modelName, error]) => {
          let message = error;
          if (modelName === 'websocket') {
            messageApi.warning('Sorry, this is taking longer than expected. If the progress bar does not start moving soon. Please try again in a few minutes.');
          } else if (modelName === 'system') {
            message = 'Some components may take longer to load';
          }
          
          return (
            <div key={modelName} style={{ 
              fontSize: '14px',
              color: '#0066cc',
              marginBottom: '4px'
            }}>
              {message}
            </div>
          );
        })}
      </div>
    );
  },[loadingErrors, messageApi]);

  // Show progress bar only when not authenticating
  const renderProgressBar = useMemo(() => {
    if (isAuthenticating) return null;

    return (
      <div style={{ 
        width: '300px', 
        height: '8px', 
        backgroundColor: '#ddd', 
        borderRadius: '4px',
        margin: '0 auto',
        overflow: 'hidden'
      }}>
                    <div style={{
            width: `${Math.max(0, Math.min(100, loadingProgress))}%`,
            height: '100%',
            background: loadingProgress === 100 ? 
              'linear-gradient(90deg, #6B54F9, #8B5CF6, #A855F7, #C084FC)' :
              'linear-gradient(90deg, #6B54F9, #8B5CF6)',
            transition: 'width 0.3s ease'
          }} />
      </div>
    );
  },[isAuthenticating, loadingProgress]);

  return (
    <div className="splash-container">
      <div className="splash-content">
        {/* Left Column - Loading Details */}
        <div className="splash-loading-section">
          <div className="splash-loading-wrapper">
            <div className="splash-logo">
              <img 
                src={logoImage} 
                alt="Busable" 
                className={`splash-logo-image ${hoverStates.logo ? 'hover' : ''}`}
                onMouseEnter={() => handleHover('logo', true)}
                onMouseLeave={() => handleHover('logo', false)}
              />
            </div>
            
            <div className="splash-loading-center">
              <div className="splash-loading-animation">
                <LoadMessage 
                  message="Loading busable workspace..." 
                  size="lg"
                />
              </div>
              
              <div className="splash-loading-details">
                {/* {renderProgressBar} */}
                
                {/* <div className="loading-message">
                  {loadingMessages[currentLoadingMessage]}
                </div> */}
                
                {renderErrors}
              </div>
            </div>
            
            <div className="footer-message">
              {footerMessages[currentFooterMessage]}
            </div>
            
            {/* {showSlowConnection && (
              <div className="slow-connection-warning">
                ⚠️ Your connection seems slow. This may take a bit longer than usual.
              </div>
            )} */}
          </div>
        </div>
        
        {/* Right Column - Product Carousel */}
        <div className="splash-carousel-section">
          <div className="carousel-content">
            <div className="carousel-header">
              <h2>Smart Transport. Seamless Operations.</h2>
              <p className="carousel-subtitle">Transform your transport operations with intelligent automation</p>
            </div>
            
            <Carousel 
              autoplay 
              autoplaySpeed={5000}
              dots={false}
              effect="fade"
              className="splash-carousel"
              dotPosition="bottom"
              easing="ease-in-out"
            >
              {carouselSlides.map((slide, index) => (
                <div key={'splash-slide-'+index} className="carousel-slide-container">
                  <div className="carousel-slide">
                    <div className="carousel-icon">
                      <img src={slide.icon} alt={slide.name} />
                    </div>
                    <div className="carousel-text">
                      <h3>{slide.name}</h3>
                      <p>{slide.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      </div>
      
      {/* Version info in bottom left corner */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        fontSize: '12px',
        color: 'rgba(255,255,255, 0.6)',
        fontFamily: 'monospace',
        pointerEvents: 'auto',
        userSelect: 'none',
        zIndex: 1000
      }}>
        <Version /> {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}
      </div>
      
    </div>
  );
}

export default SplashPage; 